import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";

const LivePlayer = ({ stream, onClose }) => {
    const videoRef = useRef(null);
    const hlsRef = useRef(null);

    const [activeTab, setActiveTab] = useState("player"); // "player", "iframe", "endpoints"
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const [streamStatus, setStreamStatus] = useState("connecting"); // "connecting", "live", "error"
    const [copiedKey, setCopiedKey] = useState(null);

    // Resolve HLS stream URL:
    // If the stream is local or RTMP, connect to our local MediaMTX HLS stream
    const isLocalStream = !stream?.live || stream.live.includes("localhost") || stream.live.includes("127.0.0.1") || stream.live.startsWith("rtmp://");
    const hlsUrl = isLocalStream
        ? "http://localhost:8888/live/stream/index.m3u8"
        : (stream?.hls || stream?.live || "http://localhost:8888/live/stream/index.m3u8");

    const rtmpUrl = (stream?.live && stream.live.startsWith("rtmp://"))
        ? stream.live
        : "rtmp://localhost:1935/live/stream";

    const iframeUrl = "http://localhost:8888/live/stream/";

    useEffect(() => {
        if (activeTab !== "player") return;

        let video = videoRef.current;
        if (!video) return;

        setStreamStatus("connecting");

        if (Hls.isSupported()) {
            if (hlsRef.current) {
                hlsRef.current.destroy();
            }

            const hls = new Hls({
                enableWorker: true,
                lowLatencyMode: true,
                backBufferLength: 30,
                maxBufferLength: 10,
                liveSyncDurationCount: 3,
                liveMaxLatencyDurationCount: 6
            });

            hlsRef.current = hls;
            hls.loadSource(hlsUrl);
            hls.attachMedia(video);

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                setStreamStatus("live");
                video.play().then(() => setIsPlaying(true)).catch(() => {
                    // Autoplay with audio was blocked by browser, try muted
                    video.muted = true;
                    setIsMuted(true);
                    video.play().then(() => setIsPlaying(true)).catch(() => {});
                });
            });

            hls.on(Hls.Events.ERROR, (event, data) => {
                if (data.fatal) {
                    console.warn("HLS fatal error:", data.type);
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            setStreamStatus("error");
                            break;
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            hls.recoverMediaError();
                            break;
                        default:
                            hls.destroy();
                            setStreamStatus("error");
                            break;
                    }
                }
            });
        } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
            // Native Safari HLS
            video.src = hlsUrl;
            video.addEventListener("loadedmetadata", () => {
                setStreamStatus("live");
                video.play().then(() => setIsPlaying(true)).catch(() => {});
            });
            video.addEventListener("error", () => {
                setStreamStatus("error");
            });
        }

        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, [hlsUrl, activeTab]);

    const togglePlay = () => {
        if (!videoRef.current) return;
        if (isPlaying) {
            videoRef.current.pause();
            setIsPlaying(false);
        } else {
            videoRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
        }
    };

    const toggleMute = () => {
        if (!videoRef.current) return;
        videoRef.current.muted = !isMuted;
        setIsMuted(!isMuted);
    };

    const handleFullScreen = () => {
        if (!videoRef.current) return;
        if (videoRef.current.requestFullscreen) {
            videoRef.current.requestFullscreen();
        }
    };

    const copyToClipboard = (text, key) => {
        navigator.clipboard.writeText(text);
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(null), 2500);
    };

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div
                className="modal-box"
                style={{
                    maxWidth: "720px",
                    width: "95%",
                    padding: 0,
                    overflow: "hidden",
                    backgroundColor: "var(--bg-surface)",
                    border: "1px solid var(--border-medium)",
                    boxShadow: "0 25px 60px rgba(0, 0, 0, 0.7)"
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Modal Header */}
                <div style={{
                    padding: "16px 20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid var(--border-subtle)",
                    backgroundColor: "rgba(255, 255, 255, 0.02)"
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <div style={{
                            width: "10px",
                            height: "10px",
                            borderRadius: "50%",
                            backgroundColor: streamStatus === "live" ? "#10b981" : (streamStatus === "connecting" ? "#f59e0b" : "#ef4444"),
                            boxShadow: streamStatus === "live" ? "0 0 10px #10b981" : "none"
                        }} />
                        <h3 style={{ margin: 0, fontSize: "16px", color: "#fff", fontWeight: 700 }}>
                            Live Broadcast: {stream?.name || "Sample Movie Stream"}
                        </h3>
                        <span style={{
                            fontSize: "11px",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            backgroundColor: "rgba(99, 102, 241, 0.15)",
                            color: "var(--accent-primary)",
                            fontFamily: "var(--font-mono)",
                            fontWeight: 600
                        }}>
                            {stream?.code || "nanbatv"}
                        </span>
                    </div>

                    <button
                        className="modal-close-btn"
                        onClick={onClose}
                        style={{ background: "none", border: "none", color: "var(--text-muted)", fontSize: "22px", cursor: "pointer" }}
                    >
                        &times;
                    </button>
                </div>

                {/* Subnav Tabs */}
                <div style={{
                    display: "flex",
                    gap: "6px",
                    padding: "10px 20px 0 20px",
                    backgroundColor: "rgba(0,0,0,0.2)",
                    borderBottom: "1px solid var(--border-subtle)"
                }}>
                    <button
                        onClick={() => setActiveTab("player")}
                        style={{
                            background: "none",
                            border: "none",
                            borderBottom: activeTab === "player" ? "2px solid var(--accent-primary)" : "2px solid transparent",
                            color: activeTab === "player" ? "#fff" : "var(--text-muted)",
                            padding: "8px 14px",
                            fontSize: "13px",
                            fontWeight: 600,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px"
                        }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polygon points="5 3 19 12 5 21 5 3"></polygon>
                        </svg>
                        HTML5 Live Monitor
                    </button>

                    <button
                        onClick={() => setActiveTab("iframe")}
                        style={{
                            background: "none",
                            border: "none",
                            borderBottom: activeTab === "iframe" ? "2px solid var(--accent-primary)" : "2px solid transparent",
                            color: activeTab === "iframe" ? "#fff" : "var(--text-muted)",
                            padding: "8px 14px",
                            fontSize: "13px",
                            fontWeight: 600,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px"
                        }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                            <line x1="8" y1="21" x2="16" y2="21"></line>
                            <line x1="12" y1="17" x2="12" y2="21"></line>
                        </svg>
                        MediaMTX Web Player
                    </button>

                    <button
                        onClick={() => setActiveTab("endpoints")}
                        style={{
                            background: "none",
                            border: "none",
                            borderBottom: activeTab === "endpoints" ? "2px solid var(--accent-primary)" : "2px solid transparent",
                            color: activeTab === "endpoints" ? "#fff" : "var(--text-muted)",
                            padding: "8px 14px",
                            fontSize: "13px",
                            fontWeight: 600,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px"
                        }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="2" y1="12" x2="22" y2="12"></line>
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                        </svg>
                        Ingest & Streaming URLs
                    </button>
                </div>

                {/* Tab 1: Embedded HTML5 Player */}
                {activeTab === "player" && (
                    <div style={{ padding: "18px 20px" }}>
                        <div style={{
                            position: "relative",
                            width: "100%",
                            aspectRatio: "16 / 9",
                            backgroundColor: "#000",
                            borderRadius: "10px",
                            overflow: "hidden",
                            border: "1px solid var(--border-medium)"
                        }}>
                            <video
                                ref={videoRef}
                                playsInline
                                muted={isMuted}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                    display: "block"
                                }}
                            />

                            {/* Top Status Overlays */}
                            <div style={{
                                position: "absolute",
                                top: "12px",
                                left: "14px",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                zIndex: 10
                            }}>
                                <span style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    padding: "3px 10px",
                                    borderRadius: "12px",
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    letterSpacing: "0.5px",
                                    backgroundColor: streamStatus === "live" ? "rgba(239, 68, 68, 0.9)" : "rgba(245, 158, 11, 0.9)",
                                    color: "#fff"
                                }}>
                                    <span style={{
                                        width: "6px",
                                        height: "6px",
                                        borderRadius: "50%",
                                        backgroundColor: "#fff",
                                        animation: streamStatus === "live" ? "pulse 1.2s infinite" : "none"
                                    }}></span>
                                    {streamStatus === "live" ? "LIVE ON-AIR" : (streamStatus === "connecting" ? "CONNECTING..." : "OFFLINE")}
                                </span>

                                <span style={{
                                    padding: "3px 8px",
                                    borderRadius: "4px",
                                    backgroundColor: "rgba(0, 0, 0, 0.65)",
                                    backdropFilter: "blur(4px)",
                                    color: "var(--accent-cyan)",
                                    fontSize: "11px",
                                    fontFamily: "var(--font-mono)"
                                }}>
                                    1080p • 24fps • H.264
                                </span>
                            </div>

                            {/* Center Error / Loading Overlay */}
                            {streamStatus === "connecting" && (
                                <div style={{
                                    position: "absolute",
                                    inset: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "rgba(0,0,0,0.7)",
                                    color: "#fff",
                                    zIndex: 5
                                }}>
                                    <div style={{
                                        width: "36px",
                                        height: "36px",
                                        border: "3px solid rgba(255,255,255,0.2)",
                                        borderTopColor: "var(--accent-primary)",
                                        borderRadius: "50%",
                                        animation: "spin 0.8s linear infinite"
                                    }} />
                                    <span style={{ marginTop: "12px", fontSize: "13px", fontWeight: 500 }}>
                                        Synchronizing Live Stream Segments...
                                    </span>
                                </div>
                            )}

                            {streamStatus === "error" && (
                                <div style={{
                                    position: "absolute",
                                    inset: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "rgba(10, 15, 30, 0.9)",
                                    color: "#fff",
                                    padding: "20px",
                                    textAlign: "center",
                                    zIndex: 5
                                }}>
                                    <div style={{
                                        width: "48px",
                                        height: "48px",
                                        borderRadius: "50%",
                                        backgroundColor: "rgba(239, 68, 68, 0.2)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginBottom: "12px"
                                    }}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <line x1="12" y1="8" x2="12" y2="12"></line>
                                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                        </svg>
                                    </div>
                                    <div style={{ fontSize: "14px", fontWeight: 700, marginBottom: "4px" }}>
                                        Live Stream Offline or Waiting for Ingest
                                    </div>
                                    <div style={{ fontSize: "12px", color: "var(--text-muted)", maxWidth: "400px", marginBottom: "16px" }}>
                                        Start the live stream simulator from the project root with <code>./scripts/stream.sh start</code> or check the MediaMTX RTMP container.
                                    </div>
                                    <button
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setStreamStatus("connecting");
                                            if (hlsRef.current) hlsRef.current.loadSource(hlsUrl);
                                        }}
                                        style={{ fontSize: "12px" }}
                                    >
                                        Retry Connection
                                    </button>
                                </div>
                            )}

                            {/* Bottom Controls Bar */}
                            <div style={{
                                position: "absolute",
                                bottom: 0,
                                left: 0,
                                right: 0,
                                padding: "10px 14px",
                                background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                zIndex: 10
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <button
                                        onClick={togglePlay}
                                        style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center" }}
                                        title={isPlaying ? "Pause" : "Play"}
                                    >
                                        {isPlaying ? (
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                                <rect x="6" y="4" width="4" height="16"></rect>
                                                <rect x="14" y="4" width="4" height="16"></rect>
                                            </svg>
                                        ) : (
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                                <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                            </svg>
                                        )}
                                    </button>

                                    <button
                                        onClick={toggleMute}
                                        style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center" }}
                                        title={isMuted ? "Unmute Audio" : "Mute Audio"}
                                    >
                                        {isMuted ? (
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="1" y1="1" x2="23" y2="23"></line>
                                                <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
                                                <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path>
                                                <line x1="12" y1="19" x2="12" y2="23"></line>
                                                <line x1="8" y1="23" x2="16" y2="23"></line>
                                            </svg>
                                        ) : (
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                                                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                                            </svg>
                                        )}
                                    </button>

                                    <span style={{ fontSize: "11px", color: "#ccc", marginLeft: "4px" }}>
                                        {isMuted ? "(Audio Muted - Click to Unmute)" : "(Live Audio On)"}
                                    </span>
                                </div>

                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <button
                                        onClick={handleFullScreen}
                                        style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center" }}
                                        title="Fullscreen"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Stream metadata row */}
                        <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginTop: "12px",
                            padding: "10px 14px",
                            backgroundColor: "rgba(255,255,255,0.03)",
                            borderRadius: "8px",
                            border: "1px solid var(--border-subtle)"
                        }}>
                            <div>
                                <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>HLS Live Feed: </span>
                                <code style={{ fontSize: "12px", color: "var(--accent-cyan)" }}>{hlsUrl}</code>
                            </div>
                            <button
                                className="btn btn-secondary"
                                style={{ padding: "4px 10px", fontSize: "11px" }}
                                onClick={() => copyToClipboard(hlsUrl, "hls")}
                            >
                                {copiedKey === "hls" ? "Copied!" : "Copy HLS"}
                            </button>
                        </div>
                    </div>
                )}

                {/* Tab 2: MediaMTX Direct Iframe */}
                {activeTab === "iframe" && (
                    <div style={{ padding: "18px 20px" }}>
                        <div style={{
                            width: "100%",
                            aspectRatio: "16 / 9",
                            backgroundColor: "#000",
                            borderRadius: "10px",
                            overflow: "hidden",
                            border: "1px solid var(--border-medium)"
                        }}>
                            <iframe
                                src={iframeUrl}
                                title="MediaMTX Web Player"
                                style={{ width: "100%", height: "100%", border: "none" }}
                                allow="autoplay; fullscreen"
                            />
                        </div>
                        <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "10px", textAlign: "center" }}>
                            Streaming directly from MediaMTX Low-Latency HLS Gateway at <code>{iframeUrl}</code>
                        </div>
                    </div>
                )}

                {/* Tab 3: Endpoints & Testing Tools */}
                {activeTab === "endpoints" && (
                    <div style={{ padding: "18px 20px" }}>
                        <div className="form-group" style={{ marginBottom: "16px" }}>
                            <label className="form-label">RTMP Ingest / Playback URL (ExoPlayer & VLC)</label>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <input
                                    type="text"
                                    readOnly
                                    className="form-control"
                                    value={rtmpUrl}
                                    style={{ fontFamily: "var(--font-mono)", fontSize: "12px" }}
                                />
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => copyToClipboard(rtmpUrl, "rtmp")}
                                >
                                    {copiedKey === "rtmp" ? "Copied!" : "Copy"}
                                </button>
                            </div>
                        </div>

                        <div className="form-group" style={{ marginBottom: "16px" }}>
                            <label className="form-label">HLS Stream Playlist (.m3u8)</label>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <input
                                    type="text"
                                    readOnly
                                    className="form-control"
                                    value={hlsUrl}
                                    style={{ fontFamily: "var(--font-mono)", fontSize: "12px" }}
                                />
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => copyToClipboard(hlsUrl, "hls-tab")}
                                >
                                    {copiedKey === "hls-tab" ? "Copied!" : "Copy"}
                                </button>
                            </div>
                        </div>

                        <div style={{
                            backgroundColor: "var(--bg-surface-elevated)",
                            padding: "14px 16px",
                            borderRadius: "8px",
                            border: "1px solid var(--border-subtle)",
                            fontSize: "12.5px"
                        }}>
                            <div style={{ fontWeight: 700, color: "#fff", marginBottom: "8px" }}>
                                Command Line Testing:
                            </div>

                            <div style={{ marginBottom: "8px" }}>
                                <span style={{ color: "var(--text-muted)", fontSize: "11px", display: "block" }}>Play via ffplay:</span>
                                <code style={{ color: "var(--accent-cyan)", fontFamily: "var(--font-mono)", fontSize: "12px" }}>
                                    ffplay "{rtmpUrl}"
                                </code>
                            </div>

                            <div style={{ marginBottom: "8px" }}>
                                <span style={{ color: "var(--text-muted)", fontSize: "11px", display: "block" }}>Play via VLC:</span>
                                <code style={{ color: "var(--accent-cyan)", fontFamily: "var(--font-mono)", fontSize: "12px" }}>
                                    vlc "{hlsUrl}"
                                </code>
                            </div>

                            <div>
                                <span style={{ color: "var(--text-muted)", fontSize: "11px", display: "block" }}>Stream control command:</span>
                                <code style={{ color: "var(--accent-violet)", fontFamily: "var(--font-mono)", fontSize: "12px" }}>
                                    ./scripts/stream.sh status
                                </code>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal Footer */}
                <div style={{
                    padding: "14px 20px",
                    borderTop: "1px solid var(--border-subtle)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: "rgba(0,0,0,0.15)"
                }}>
                    <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                        Source: <code>Mandaadi (2026) 1080p Local Video</code>
                    </span>
                    <button type="button" className="btn btn-secondary" onClick={onClose}>
                        Close Monitor
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LivePlayer;
